import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type AutomationUsageResult } from '@whinerp/client';
import { formatCount } from '@whinerp/contracts/format';
import { Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/** Setup > Automation limits (F-096): usage meters against the hourly caps. */
export function AutomationLimitsPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const enabled = useEntitlement('F-096');
  const { can } = useSession();
  const allowed = enabled.enabled && can('automation.manage');
  const usage = useQuery<AutomationUsageResult, ApiError>({ queryKey: ['automation-usage'], queryFn: () => api.automationUsage({}), enabled: allowed });
  if (!allowed) return null;
  const meter = (key: string, used: number, cap: number, share: number) => (
    <li data-meter={key} className="flex flex-col gap-1">
      <span className="text-dense text-ink">
        {t(`governor.${key}` as 'governor.runs')}: {formatCount(used, format)} / {formatCount(cap, format)}
      </span>
      <span className="h-2 w-full rounded-control bg-canvas">
        <span className={share >= 0.8 ? 'block h-2 rounded-control bg-warning' : 'block h-2 rounded-control bg-bronze'} style={{ width: `${String(Math.min(100, Math.round(share * 100)))}%` }} />
      </span>
    </li>
  );
  return (
    <Panel data-region="automation-limits">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('governor.title')}</SectionLabel>
        {usage.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : usage.isError ? (
          <p className="text-dense text-danger-text">{errorText(usage.error)}</p>
        ) : (
          <>
            <ul className="flex flex-col gap-2">
              {meter('runs', usage.data.runsLastHour, usage.data.limits.runsPerHour, usage.data.runShare)}
              {meter('calls', usage.data.externalCallsLastHour, usage.data.limits.externalCallsPerHour, usage.data.callShare)}
            </ul>
            <p className="text-meta text-ink-meta">{t('governor.steps', { values: { steps: formatCount(usage.data.limits.stepsPerRun, format) } })}</p>
          </>
        )}
      </div>
    </Panel>
  );
}
